import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, Package, Star, Truck, MapPin, Clock, Calendar, XCircle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const driverPhotoUrl = (path) => (path ? `${API}/files/${path}` : null);

// Format ISO timestamp → Arabic-friendly Baghdad-local date + time.
// Used in the customer's "My Orders" list (Feb 14, 2026 — added per
// request: show date + time on each order card).
const fmtOrderDateTime = (iso) => {
  if (!iso) return { date: "—", time: "—" };
  try {
    const dt = new Date(iso);
    const datePart = dt.toLocaleDateString("ar-IQ-u-nu-latn", {
      weekday: "short", year: "numeric", month: "short", day: "numeric",
      timeZone: "Asia/Baghdad",
    });
    const timePart = dt.toLocaleTimeString("en-US", {
      hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Baghdad",
    });
    return { date: datePart, time: timePart };
  } catch {
    return { date: (iso || "").slice(0, 10), time: (iso || "").slice(11, 16) };
  }
};

const statusLabels = {
  pending: { label: "قيد المعالجة", color: "#F5A623" },
  confirmed: { label: "تم موافقة الطلب", color: "#4A90E2" },
  delivering: { label: "قيد التوصيل", color: "#E85D45" },
  delivered: { label: "تم التوصيل", color: "#7ED321" },
  rejected: { label: "مرفوض", color: "#999" },
};

// Map star → tier (visual feedback only; backend stores rating)
const tierFromRating = (r) => {
  if (r >= 5) return { name: "ذهبي", emoji: "🥇", color: "#F5A623" };
  if (r >= 4) return { name: "فضي", emoji: "🥈", color: "#9CA3AF" };
  if (r >= 1) return { name: "برونزي", emoji: "🥉", color: "#B87333" };
  return null;
};

const RatingModal = ({ order, onClose, onRated }) => {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (rating < 1) {
      toast.error("اختر عدد النجوم");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/orders/${order.id}/rate-driver`, { rating, comment });
      toast.success("شكراً على تقييمك!");
      onRated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل التقييم");
    } finally {
      setBusy(false);
    }
  };

  const currentTier = tierFromRating(hover || rating);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-4" data-testid="rating-modal">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl"
      >
        <div className="text-center mb-4">
          {driverPhotoUrl(order.driver_photo_path) ? (
            <img
              src={driverPhotoUrl(order.driver_photo_path)}
              alt={order.driver_name || "السائق"}
              data-testid="rating-driver-photo"
              className="w-24 h-24 rounded-full object-cover mx-auto mb-3 border-4 border-[#3B82F6] shadow-lg"
            />
          ) : (
            <div className="w-24 h-24 rounded-full bg-[#F0F9FF] border-4 border-[#3B82F6] mx-auto mb-3 flex items-center justify-center">
              <Truck className="w-10 h-10 text-[#3B82F6]" />
            </div>
          )}
          <h3 className="font-heading font-bold text-xl text-[#111814]">
            {order.driver_name || "السائق"}
          </h3>
          <p className="text-sm text-[#5C6E64] mt-1">قيّم تجربتك مع السائق</p>
        </div>

        <div className="flex justify-center gap-2 my-6" data-testid="stars-row">
          {[1, 2, 3, 4, 5].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setRating(s)}
              onMouseEnter={() => setHover(s)}
              onMouseLeave={() => setHover(0)}
              data-testid={`star-${s}`}
              className="transition-transform hover:scale-110"
            >
              <Star
                className="w-10 h-10"
                fill={(hover || rating) >= s ? "#F5A623" : "none"}
                stroke={(hover || rating) >= s ? "#F5A623" : "#E4EBE6"}
                strokeWidth={2}
              />
            </button>
          ))}
        </div>

        {currentTier && (
          <div
            className="text-center rounded-2xl py-2 mb-4 font-bold"
            style={{ backgroundColor: `${currentTier.color}20`, color: currentTier.color }}
            data-testid="tier-preview"
          >
            {currentTier.emoji} المستوى: {currentTier.name}
          </div>
        )}

        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="اكتب تعليقك (اختياري)..."
          data-testid="rating-comment"
          className="w-full px-4 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#3B82F6] bg-white text-sm resize-none"
          rows={3}
        />

        <div className="grid grid-cols-2 gap-2 mt-4">
          <button
            onClick={onClose}
            disabled={busy}
            className="bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] font-bold py-3 rounded-full"
            data-testid="rating-cancel"
          >
            لاحقاً
          </button>
          <button
            onClick={submit}
            disabled={busy || rating < 1}
            className="bg-[#3B82F6] hover:bg-[#2563EB] text-white font-bold py-3 rounded-full disabled:opacity-50"
            data-testid="rating-submit"
          >
            {busy ? "جاري الإرسال..." : "إرسال التقييم"}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

const MyOrders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ratableOrders, setRatableOrders] = useState([]);
  const [todayDrivers, setTodayDrivers] = useState([]);
  const [me, setMe] = useState(null);
  const [activeRating, setActiveRating] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [oRes, rRes, tdRes, meRes] = await Promise.all([
        axios.get(`${API}/orders/my`),
        axios.get(`${API}/orders/ratable`).catch(() => ({ data: [] })),
        axios.get(`${API}/orders/today-drivers`).catch(() => ({ data: [] })),
        axios.get(`${API}/auth/me`).catch(() => ({ data: null })),
      ]);
      setOrders(oRes.data);
      setRatableOrders(rRes.data || []);
      setTodayDrivers(tdRes.data || []);
      setMe(meRes.data);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  return (
    <div className="min-h-screen bg-transparent">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-[#E4EBE6]">
        <div className="max-w-md mx-auto md:max-w-2xl lg:max-w-3xl px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            data-testid="orders-back"
            className="p-2 hover:bg-[#F3F6F4] rounded-full transition-colors"
          >
            <ArrowRight className="w-5 h-5 text-[#2D4A3E]" />
          </button>
          <h1 className="font-heading font-bold text-2xl text-[#2D4A3E]">طلباتي</h1>
        </div>
      </header>

      <main className="max-w-md mx-auto md:max-w-2xl lg:max-w-3xl px-4 py-6 space-y-4">
        {/* Customer Info Card */}
        {me && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="customer-info-card"
            className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white rounded-2xl p-4"
          >
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-[10px] opacity-80">الزبون</p>
                <p className="font-bold text-sm truncate">{me.full_name}</p>
              </div>
              <div>
                <p className="text-[10px] opacity-80">الماركت</p>
                <p className="font-bold text-sm truncate">{me.market_name || "—"}</p>
              </div>
              <div>
                <p className="text-[10px] opacity-80">رقم الايباد</p>
                <p className="font-bold text-sm">{me.customer_id || "—"}</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Today's Delivery Drivers */}
        {todayDrivers.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="today-drivers-card"
            className="bg-white border border-[#BAE6FD] rounded-2xl p-4 space-y-3"
          >
            <h3 className="font-heading font-bold text-base text-[#111814] flex items-center gap-2">
              <Truck className="w-5 h-5 text-[#3B82F6]" />
              السائقون الذين وصّلوا طلباتك اليوم
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {todayDrivers.map((d) => (
                <div
                  key={d.driver_id}
                  data-testid={`today-driver-${d.driver_id}`}
                  className="flex items-center gap-3 bg-[#F0F9FF] rounded-xl p-2.5"
                >
                  {driverPhotoUrl(d.photo_path) ? (
                    <img
                      src={driverPhotoUrl(d.photo_path)}
                      alt={d.driver_name}
                      className="w-12 h-12 rounded-full object-cover border-2 border-[#3B82F6] flex-shrink-0"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-white border-2 border-[#3B82F6] flex items-center justify-center flex-shrink-0">
                      <Truck className="w-5 h-5 text-[#3B82F6]" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm text-[#111814] truncate">{d.driver_name}</p>
                    <p className="text-xs text-[#5C6E64]">
                      {d.orders_delivered_today} طلب · {Number(d.total_amount).toLocaleString("en-US", { maximumFractionDigits: 0 })} د.ع
                    </p>
                  </div>
                  {!d.rated && (
                    <button
                      onClick={() => {
                        const ord = ratableOrders.find((o) => o.id === d.last_order_id) || ratableOrders.find((o) => o.driver_id === d.driver_id);
                        if (ord) setActiveRating(ord);
                      }}
                      data-testid={`quick-rate-${d.driver_id}`}
                      className="bg-[#3B82F6] hover:bg-[#2563EB] text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1"
                    >
                      <Star className="w-3 h-3" fill="#F5A623" stroke="#F5A623" />
                      تقييم
                    </button>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {ratableOrders.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="ratable-banner"
            className="bg-gradient-to-l from-[#3B82F6] to-[#2563EB] text-white rounded-2xl p-4"
          >
            <p className="font-bold mb-2 flex items-center gap-2">
              <Star className="w-5 h-5" fill="#F5A623" stroke="#F5A623" />
              لديك {ratableOrders.length} طلب بانتظار التقييم
            </p>
            <p className="text-sm opacity-90 mb-3">قيّم السائقين لمساعدتنا في تحسين الخدمة</p>
            <button
              onClick={() => setActiveRating(ratableOrders[0])}
              data-testid="rate-now-btn"
              className="bg-white text-[#3B82F6] font-bold py-2 px-4 rounded-full text-sm"
            >
              قيّم الآن
            </button>
          </motion.div>
        )}

        {loading ? (
          <p className="text-center text-[#5C6E64]">جاري التحميل...</p>
        ) : orders.length === 0 ? (
          <div className="text-center py-16">
            <Package className="w-20 h-20 text-[#E4EBE6] mx-auto mb-4" />
            <p className="text-lg text-[#5C6E64] mb-6">لا توجد طلبات</p>
            <Link
              to="/"
              className="inline-block bg-[#2D4A3E] hover:bg-[#233A30] text-white font-medium px-8 py-3 rounded-full transition-colors"
            >
              ابدأ التسوق
            </Link>
          </div>
        ) : (
          orders.map((order, idx) => {
            const canRate =
              order.status === "delivered" &&
              order.driver_id &&
              !order.driver_rating;
            return (
              <motion.div
                key={order.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                data-testid={`order-${order.id}`}
                className="bg-[#FAFCFA] border border-[#E4EBE6] rounded-2xl p-5 space-y-3"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs text-[#5C6E64]">رقم الطلب</p>
                    <p className="font-mono text-sm font-bold text-[#111814]">
                      {order.id.slice(0, 8).toUpperCase()}
                    </p>
                  </div>
                  <span
                    className="px-3 py-1 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: `${statusLabels[order.status]?.color}20`,
                      color: statusLabels[order.status]?.color,
                    }}
                  >
                    {statusLabels[order.status]?.label}
                  </span>
                </div>

                {/* Date + Time strip — Feb 14, 2026: added per admin request.
                    Shows when the customer placed the order in Baghdad time. */}
                {(() => {
                  const { date, time } = fmtOrderDateTime(order.created_at);
                  return (
                    <div
                      data-testid={`order-datetime-${order.id}`}
                      className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[#5C6E64] bg-[#F0F9FF] border border-[#3B82F6]/15 rounded-xl px-3 py-1.5"
                    >
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5 text-[#3B82F6]" />
                        <span className="font-bold text-[#1E3A5F]">{date}</span>
                      </span>
                      <span className="text-[#E4EBE6]">|</span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5 text-[#3B82F6]" />
                        <span className="font-bold text-[#1E3A5F]" dir="ltr">{time}</span>
                      </span>
                    </div>
                  );
                })()}

                <div className="space-y-1 py-3 border-t border-b border-[#E4EBE6]">
                  {order.items.map((item) => (
                    <div key={item.product_id} className="flex justify-between text-sm">
                      <span className="text-[#5C6E64]">
                        {item.name_ar} × {item.quantity}
                      </span>
                      <span>
                        {(item.price * item.quantity).toLocaleString("en-US", { maximumFractionDigits: 0 })} د.ع
                      </span>
                    </div>
                  ))}
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#5C6E64]">المجموع</span>
                  <span className="font-bold text-lg text-[#E85D45]">
                    {order.total.toLocaleString("en-US", { maximumFractionDigits: 0 })} د.ع
                  </span>
                </div>

                {order.driver_rating && (
                  <div className="flex items-center gap-1 text-xs text-[#5C6E64]">
                    تقييمك للسائق:
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star
                        key={s}
                        className="w-3.5 h-3.5"
                        fill={order.driver_rating >= s ? "#F5A623" : "none"}
                        stroke={order.driver_rating >= s ? "#F5A623" : "#E4EBE6"}
                      />
                    ))}
                  </div>
                )}

                {canRate && (
                  <button
                    onClick={() => setActiveRating(order)}
                    data-testid={`rate-driver-${order.id}`}
                    className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white font-bold py-2.5 rounded-full text-sm flex items-center justify-center gap-2"
                  >
                    <Star className="w-4 h-4" fill="#F5A623" stroke="#F5A623" />
                    قيّم السائق
                  </button>
                )}

                {/* Track button for active orders with driver */}
                {order.driver_id && !activeRating &&
                  ["assigned", "accepted", "preparing", "ready", "on_the_way", "delivering", "awaiting_payment_confirmation"].includes(order.status) && (
                  <button
                    onClick={() => navigate(`/track/${order.id}`)}
                    data-testid={`track-${order.id}`}
                    className="w-full bg-gradient-to-l from-[#3B82F6] to-[#1E3A5F] hover:opacity-90 text-white font-bold py-2.5 rounded-full text-sm flex items-center justify-center gap-2"
                  >
                    <MapPin className="w-4 h-4" />
                    تتبّع الطلب على الخريطة
                  </button>
                )}

                {/* Customer cancel button — only while status is 'pending'
                    (admin hasn't confirmed yet). Feb 17, 2026 request. */}
                {order.status === "pending" && (
                  <button
                    onClick={async () => {
                      if (!window.confirm(
                        `⚠️ تأكيد إلغاء الفاتورة رقم ${order.invoice_number}؟\n\n` +
                        `سترجع البضاعة للمخزن، ولن تُخصم أرباح إضافية غير التي كسبتها من هذه الفاتورة. ` +
                        `لا يمكن التراجع عن هذا الإجراء.`
                      )) return;
                      try {
                        await axios.delete(`${API}/orders/${order.id}`);
                        toast.success("تم إلغاء الفاتورة ✓");
                        fetchAll();
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "فشل الإلغاء");
                      }
                    }}
                    data-testid={`cancel-${order.id}`}
                    className="w-full bg-[#E85D45] hover:bg-[#d54d35] text-white font-bold py-2.5 rounded-full text-sm flex items-center justify-center gap-2"
                  >
                    <XCircle className="w-4 h-4" />
                    إلغاء الفاتورة
                  </button>
                )}
                {/* Cancelled-by-customer badge */}
                {order.cancelled_by_customer && (
                  <div className="bg-[#FEE2E2] border border-[#E85D45]/40 rounded-xl px-3 py-1.5 text-[11px] text-[#B91C1C] text-center font-bold">
                    ⛔ قمت بإلغاء هذه الفاتورة
                  </div>
                )}
              </motion.div>
            );
          })
        )}
      </main>

      {activeRating && (
        <RatingModal
          order={activeRating}
          onClose={() => setActiveRating(null)}
          onRated={fetchAll}
        />
      )}
    </div>
  );
};

export default MyOrders;
